import { useCallback } from 'react';

import { ViableAccountsType, useUserConnectedAccounts } from '@givebacks/api/core';
import { useUserBalances } from '@givebacks/api/ledger';
import { BalanceCard, Modal, ModalProps, Stack } from 'ui';

import { TransferCashBackForm } from '../../../organisms/forms/TransferCashBackForm/TransferCashBackForm';

interface TransferAvailableBalanceModalProps extends ModalProps {
  modalProps: Partial<ModalProps>;

  onCreateAccount: (account: ViableAccountsType | 'hyperWallet') => void;

  /** Called when a hyperwallet account doesn't exist on onCreateAccount  */
  onPendingAccount?: (account: ViableAccountsType) => void;

  /** Called after a transfer has finished  */
  onTransferSuccess: () => void;
}

interface TransferAvailableBalanceProps
  extends Pick<
    TransferAvailableBalanceModalProps,
    'onCreateAccount' | 'onPendingAccount' | 'onTransferSuccess'
  > {}

const TransferAvailableBalance = ({
  onCreateAccount,

  onPendingAccount,
  onTransferSuccess,
}: TransferAvailableBalanceProps) => {
  const { data: balance, isLoading } = useUserBalances();
  const { data: accountsStatus } = useUserConnectedAccounts();

  const handleNoHyperWalletAccount = useCallback(
    (account: ViableAccountsType) => {
      if (!accountsStatus?.hyperwallet_user) {
        onPendingAccount?.(account);
        return onCreateAccount('hyperWallet');
      }
      return onCreateAccount(account);
    },
    [accountsStatus?.hyperwallet_user, onCreateAccount, onPendingAccount]
  );

  return (
    <Stack gap="lg">
      <BalanceCard
        isLoading={isLoading}
        available={balance?.available}
        pending={balance?.pending?.amount}
      />
      <TransferCashBackForm
        onConnectAccount={handleNoHyperWalletAccount}
        onTransferSuccess={onTransferSuccess}
      />
    </Stack>
  );
};

const TransferAvailableBalanceModal = ({
  opened,
  onClose,
  title,
  modalProps,
  onCreateAccount,

  onPendingAccount,
  onTransferSuccess,
}: TransferAvailableBalanceModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title ?? 'Transfer Available Balance'}
      {...modalProps}
    >
      <TransferAvailableBalance
        onCreateAccount={onCreateAccount}
        onPendingAccount={onPendingAccount}
        onTransferSuccess={onTransferSuccess}
      />
    </Modal>
  );
};

export { TransferAvailableBalanceModal };
